import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { RiMenu3Line, RiCloseLine } from 'react-icons/ri';
import { API_BASE_URL } from "../../config";
import { StudentNavbar, SubjectSideNavigation } from '../../components';
import complete from '../../assets/complete.svg';
import {
  getSubjectMeta,
  deslugify,
  formatExamLabel
} from '../../constants/subjectMeta';
import { scoreQuiz, formatMarks } from '../../utils/quizScore';

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : null;

const ModuleLearnSummary = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { exam_type, subject, module } = useParams();
  const examSlug = (exam_type || 'bgcse').toLowerCase();

  const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
  const studentId = userInfo?.id;

  const subjectModuleId =
    location.state?.subjectModuleId ?? location.state?.subject_module_id;

  const selectedModuleIndex =
    location.state?.moduleNumber ?? location.state?.module_number ?? 1;

  const sessionResults = location.state?.results || null;
  const sessionQuestions = location.state?.questions || null;
  const sessionVerdicts = location.state?.verdicts || null;

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Mobile dropdown state (same pattern as Biology page)
  const [showModuleMenu, setShowModuleMenu] = useState(false);

  const { 
    label: subjectDisplayName, 
    img: subjectImage 
  } = getSubjectMeta(examSlug, subject);

  const moduleDisplayName = deslugify(module);
  const examDisplayName = formatExamLabel(examSlug);

  useEffect(() => {
    if (!studentId || !subjectModuleId) {
      setLoading(false);
      return;
    }

    const fetchStats = async () => {
      try {
        setLoading(true);

        const resp = await axios.get(
          `${API_BASE_URL}/api/module_mastery_statistics`,
          {
            params: {
              student_id: studentId,
              subject_module_id: subjectModuleId,
            },
          }
        );

        setStats(resp.data || null);
      } catch (error) {
        console.error('Error fetching latest learning summary:', error);
        setStats(null);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [studentId, subjectModuleId]);

  const mastery = stats?.mastery_percentage ?? 0;
  const masteredCount = stats?.mastered_questions ?? 0;
  const inProgressCount = stats?.in_progress_questions ?? 0;
  const notStartedCount = stats?.not_started_questions ?? 0;
  const totalQuestions = stats?.total_questions ?? 0;

  const threshold = stats?.mastery_threshold ?? 80;

  const isUnlocked = stats?.unlocked;
  const isMastered = stats?.mastered ?? mastery >= threshold;

  const lastPracticedAt = fmt(stats?.last_practiced_at);
  const unlockedAt = fmt(stats?.unlocked_at);
  const masteredAt = fmt(stats?.mastered_at);

  const targetMastered = totalQuestions
    ? Math.ceil((threshold / 100) * totalQuestions)
    : 0;

  const remainingToThreshold = Math.max(0, targetMastered - masteredCount);

  const focusTip =
    notStartedCount > 0
      ? 'Start a few new questions to build coverage, then convert "Needs Review" into "Mastered".'
      : 'Focus on converting "Needs Review" into "Mastered" by redoing missed questions without help.';

  const [showDates, setShowDates] = useState(false);

  const sessionBreakdown = useMemo(() => {
    if (!Array.isArray(sessionResults)) return null;

    const counts = {
      correct: 0,
      correct_with_help: 0,
      partial: 0,
      incorrect: 0,
      skipped: 0,
      unanswered: 0,
    };

    sessionResults.forEach((r) => {
      if (r == null) counts.unanswered += 1;
      else if (counts[r] != null) counts[r] += 1;
    });

    return counts;
  }, [sessionResults]);

  // Scored in marks, not in questions — see utils/quizScore.
  const sessionScore = useMemo(() => {
    if (!Array.isArray(sessionResults) || !Array.isArray(sessionQuestions)) return null;

    const entries = sessionQuestions.map((question, idx) => {
      const outcome = sessionResults[idx];
      return {
        question,
        // Skipped and never-reached questions are left out of the score entirely,
        // matching the backend accuracy stat.
        answered: outcome != null && outcome !== 'skipped',
        correct: outcome === 'correct' || outcome === 'correct_with_help',
        verdict: sessionVerdicts?.[idx] ?? null,
      };
    });

    const scored = scoreQuiz(entries);
    return scored.possible > 0 ? scored : null;
  }, [sessionResults, sessionQuestions, sessionVerdicts]);

  let summaryContent;

  if (loading) {
    summaryContent = (
      <div className="w-full bg-white rounded-lg shadow-sm p-6 flex items-center justify-center">
        <p className="text-sm text-gray-600">Loading latest learning summary...</p>
      </div>
    );
  } else if (!studentId || !subjectModuleId || !stats) {
    summaryContent = (
      <div className="w-full bg-white rounded-lg shadow-sm p-6 flex flex-col items-center justify-center text-center">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">
          Learning Summary Unavailable
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          We couldn't load your latest learning summary for this module.
        </p>
        <button
          type="button"
          onClick={() => navigate(`/${examSlug}/${subject}/${module}/learn`)}
          className="rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 transition"
        >
          Back to Practice
        </button>
      </div>
    );
  } else {
    summaryContent = (
      <div className="w-full bg-white rounded-lg shadow-sm p-4 sm:p-6 lg:p-8">
        <h2 className="text-xl sm:text-2xl font-thin text-gray-800 mb-2 text-center">
          Latest Learning Summary
        </h2>
        <div className="mb-2">
          <div className="grid grid-cols-1 gap-4 items-center w-full">
            <p className="text-xs sm:text-sm text-gray-500 items-center justify-center text-center">
              {subjectDisplayName} • {moduleDisplayName} • {examDisplayName}
            </p>

          {/* Dates / status */}
          <div className="mt-4">
            {/* Mobile: collapsible details */}
            <div className="sm:hidden">
              <button
                type="button"
                onClick={() => setShowDates((v) => !v)}
                className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm flex items-center justify-between"
                aria-expanded={showDates}
              >
                <div className="min-w-0 text-left">
                  <p className="text-xs font-semibold text-gray-700">Details</p>
                  <p className="text-xs text-gray-500 truncate">
                    Last practiced: {lastPracticedAt || "No attempts yet"}
                  </p>
                </div>

                <svg
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className={`h-5 w-5 text-gray-400 transition-transform ${
                    showDates ? "rotate-180" : ""
                  }`}
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.06 1.06l-4.24 4.24a.75.75 0 0 1-1.06 0L5.21 8.29a.75.75 0 0 1 .02-1.08z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              <div
                className={`overflow-hidden transition-[max-height] duration-200 ${
                  showDates ? "max-h-60" : "max-h-0"
                }`}
              >
                <div className="mt-3 space-y-2">
                  <div className="w-full inline-flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2">
                    <span className="text-xs font-semibold text-gray-600">Last practiced</span>
                    <span className="text-xs text-gray-900 truncate">{lastPracticedAt || "No attempts yet"}</span>
                  </div>

                  {isUnlocked && unlockedAt && (
                    <div className="w-full inline-flex items-center justify-between gap-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-2">
                      <span className="text-xs font-semibold text-sky-700">Unlocked</span>
                      <span className="text-xs text-sky-900 truncate">{unlockedAt}</span>
                    </div>
                  )}

                  {isMastered && masteredAt && (
                    <div className="w-full inline-flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2">
                      <span className="text-xs font-semibold text-emerald-700">Mastered</span>
                      <span className="text-xs font-semibold text-emerald-900 truncate">{masteredAt}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Desktop/tablet: show pills normally */}
            <div className="hidden sm:flex flex-wrap gap-2 items-center justify-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1">
                <span className="text-[10px] sm:text-[11px] font-semibold text-gray-600">
                  Last practiced
                </span>
                <span className="text-[10px] sm:text-[11px] text-gray-900">
                  {lastPracticedAt || "No attempts yet"}
                </span>
              </div>

              {isUnlocked && unlockedAt && (
                <div className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1">
                  <span className="text-[10px] sm:text-[11px] font-semibold text-sky-700">
                    Unlocked
                  </span>
                  <span className="text-[10px] sm:text-[11px] text-sky-900">{unlockedAt}</span>
                </div>
              )}

              {isMastered && masteredAt && (
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1">
                  <span className="text-[10px] sm:text-[11px] font-semibold text-emerald-700">
                    Mastered
                  </span>
                  <span className="text-[10px] sm:text-[11px] font-semibold text-emerald-900">
                    {masteredAt}
                  </span>
                </div>
              )}
            </div>
          </div>


            <img
              src={complete}
              alt="Practice complete"
              className="mx-auto justify-center items-center w-40 sm:w-52 md:w-56 lg:w-60 h-auto select-none pointer-events-none opacity-95"
              loading="lazy"
            />
          </div>
        </div>

        {/* Mastery buckets */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 text-xs text-gray-700 mb-6">
          <div className="bg-slate-50 rounded-md p-2 sm:p-3 text-center">
            <p className="font-semibold text-emerald-700 text-[10px] sm:text-xs">Mastered</p>
            <p className="mt-1 text-base sm:text-lg font-bold text-emerald-600">{masteredCount}</p>
          </div>
          <div className="bg-slate-50 rounded-md p-2 sm:p-3 text-center">
            <p className="font-semibold text-amber-700 text-[10px] sm:text-xs">Needs Review</p>
            <p className="mt-1 text-base sm:text-lg font-bold text-amber-600">{inProgressCount}</p>
          </div>
          <div className="bg-slate-50 rounded-md p-2 sm:p-3 text-center">
            <p className="font-semibold text-gray-700 text-[10px] sm:text-xs">Not Started</p>
            <p className="mt-1 text-base sm:text-lg font-bold text-gray-600">{notStartedCount}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="mb-2 sm:mb-6 flex lg:flex-row flex-col justify-center gap-3">
          <button
            type="button"
            onClick={() => navigate(`/${examSlug}/${subject}/${module}`)}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition w-full lg:w-[50%]"
          >
            Back to Module Overview
          </button>

          <button
            type="button"
            onClick={() => navigate(`/${examSlug}/${subject}/${module}/learn`)}
            className="rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 transition w-full lg:w-[50%]"
          >
            Continue Studying
          </button>
        </div>

        {/* Next steps */}
        <div className="rounded-md border bg-slate-50 p-3 sm:p-4 mb-2 mt-2">
          <p className="text-sm font-semibold text-gray-800">Next steps</p>

          {isMastered ? (
            <p className="text-xs sm:text-sm text-gray-700 mt-1">
              You've reached <span className="font-semibold">{threshold}%</span> mastery!
              Do a short refresher later this week to keep it strong.
            </p>
          ) : (
            <>
              <p className="text-xs sm:text-sm text-gray-700 mt-1">
                You're <span className="font-semibold">{remainingToThreshold}</span>{' '}
                mastered questions away from reaching{' '}
                <span className="font-semibold">{threshold}%</span>.
              </p>
              <p className="text-[11px] sm:text-xs text-gray-600 mt-2">{focusTip}</p>
            </>
          )}
        </div>

        {/* Session breakdown */}
        {sessionBreakdown && (
          <div className="rounded-md border p-3 sm:p-4 mb-6">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
              <p className="text-sm font-semibold text-gray-800">This session</p>
              {sessionScore && (
                <p className="text-sm text-gray-600">
                  <span className="font-bold text-gray-900">
                    {sessionScore.percent.toFixed(1)}%
                  </span>{' '}
                  · {formatMarks(sessionScore.earned)} of {formatMarks(sessionScore.possible)} marks
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 text-xs text-gray-700">
              <div className="bg-emerald-50 rounded-md p-2 text-center">
                <p className="font-semibold text-emerald-700 text-[10px] sm:text-xs">Correct</p>
                <p className="text-base sm:text-lg font-bold text-emerald-600">{sessionBreakdown.correct}</p>
              </div>
              <div className="bg-sky-50 rounded-md p-2 text-center">
                <p className="font-semibold text-sky-700 text-[10px] sm:text-xs">With Help</p>
                <p className="text-base sm:text-lg font-bold text-sky-600">{sessionBreakdown.correct_with_help}</p>
              </div>
              {sessionBreakdown.partial > 0 && (
                <div className="bg-amber-50 rounded-md p-2 text-center">
                  <p className="font-semibold text-amber-700 text-[10px] sm:text-xs">Partial</p>
                  <p className="text-base sm:text-lg font-bold text-amber-600">{sessionBreakdown.partial}</p>
                </div>
              )}
              <div className="bg-red-50 rounded-md p-2 text-center">
                <p className="font-semibold text-red-700 text-[10px] sm:text-xs">Incorrect</p>
                <p className="text-base sm:text-lg font-bold text-red-600">{sessionBreakdown.incorrect}</p>
              </div>
              <div className="bg-gray-100 rounded-md p-2 text-center">
                <p className="font-semibold text-gray-700 text-[10px] sm:text-xs">Skipped</p>
                <p className="text-base sm:text-lg font-bold text-gray-700">{sessionBreakdown.skipped}</p>
              </div>
              <div className="bg-gray-50 rounded-md p-2 text-center col-span-2 sm:col-span-1">
                <p className="font-semibold text-gray-700 text-[10px] sm:text-xs">Unanswered</p>
                <p className="text-base sm:text-lg font-bold text-gray-700">{sessionBreakdown.unanswered}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <StudentNavbar />

      <div className="bg-white w-full">
        {/* Responsive grid: 1 col on small, 5 cols on lg+ */}
        <div className="grid grid-cols-1 lg:grid-cols-5">
          {/* Desktop side-nav */}
          <div className="hidden lg:block lg:col-span-1 text-sm">
            <SubjectSideNavigation
              selectedModuleIndex={selectedModuleIndex}
              subjectName={subjectDisplayName}
              img={subjectImage}
            />
          </div>

          {/* Main content */}
          <div className="col-span-1 lg:col-span-4 bg-[#f0f0f0] px-3 sm:px-5 py-3 sm:py-5 overflow-y-auto min-h-screen">
            {/* Mobile dropdown (instead of “just hiding it”) */}
            <div className="lg:hidden mb-4">
              <button
                onClick={() => setShowModuleMenu((s) => !s)}
                className="w-full bg-white rounded-lg shadow-sm px-4 py-3 flex items-center justify-between text-gray-700 hover:bg-gray-50 transition"
              >
                <div className="flex items-center gap-3">
                  <img
                    img={subjectImage}
                    alt={subjectDisplayName}
                    className="h-8 w-8 rounded object-cover"
                  />
                  <div className="flex flex-col items-start leading-tight">
                    <span className="font-semibold">{subjectDisplayName} Modules</span>
                    <span className="text-xs text-gray-500">
                      Currently: {moduleDisplayName}
                    </span>
                  </div>
                </div>

                {showModuleMenu ? (
                  <RiCloseLine size={24} className="text-gray-600" />
                ) : (
                  <RiMenu3Line size={24} className="text-gray-600" />
                )}
              </button>

              {showModuleMenu && (
                <div className="mt-2 bg-white rounded-lg shadow-lg overflow-hidden animate-scale-down max-h-[60vh] overflow-y-auto">
                  <SubjectSideNavigation
                    selectedModuleIndex={selectedModuleIndex}
                    subjectName={subjectDisplayName}
                    img={subjectImage}
                  />
                </div>
              )}
            </div>

            <div className="w-full max-w-4xl mx-auto">{summaryContent}</div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes scale-down {
          0% {
            transform: scaleY(0);
            opacity: 0;
            transform-origin: top;
          }
          100% {
            transform: scaleY(1);
            opacity: 1;
            transform-origin: top;
          }
        }
        .animate-scale-down {
          animation: scale-down 0.2s ease-out both;
        }
      `}</style>
    </div>
  );
};

export default ModuleLearnSummary;
